import { type JSX,SyntheticEvent, useEffect, useState } from 'react';
import { Outlet, useLocation,useParams } from 'react-router-dom';

import { CircularProgress, Stack, Tabs,Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { NotFound } from 'common/components/NotFound';
import { Page } from 'common/components/Page';
import { selectPageInfo, setPageInfo, setPaginationGridData } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { StyledBreadcrumbs } from 'common/components/StyledBreadcrumbs';
import { StyledTab } from 'common/components/StyledTab';
import { Routes, RoutesNameMap } from 'common/constants/routes';
import { is404Error, isSomethingWentWrongError } from 'common/services/api/apiHelpers';
import { fetchLicenseDetails } from 'common/services/api/Licenses/Licenses.thunk';
import { FetchLicenseDetailsResponse } from 'common/services/api/Licenses/LicenseThunkTypeDefs';
import { replaceLocation } from 'common/utils/locationHelpers';

import { LicenseDetailsHeaderColumn } from './components/LicenseDetailsHeaderColumn';
import { _createLicenseDetailsHeaderRowItemsConfig } from './LicenseDetailsHelpers';

export function LicenseDetails(): JSX.Element {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [licenseDetailsCustomLabelItemsToRender, setLicenseDetailsCustomLabelItemsToRender] = useState<React.ReactElement<any>[]>([]);
    const [currentTab, setCurrentTab] = useState<Routes>(Routes.Users);
    const [processing, setProcessing] = useState<boolean>(true);
    const [error, setError] = useState<string>('');

    const location = useLocation();
    const { suiteId = '', tenantId = '', licenseId = '' } = useParams();
    const dispatch = useAppDispatch();
    const licenseDetails = useAppSelector(selectPageInfo) as FetchLicenseDetailsResponse;

    const displayAclTab = licenseDetails?.tenantRequiresLicense;
    useEffect(() => {
        const replacedLastSegment = replaceLocation(
            lastPathSegment => lastPathSegment === licenseId,
            `${location.pathname}/${Routes.Users}`);
        setCurrentTab(replacedLastSegment as Routes);
    }, [location]);

    useEffect(() => {
        const fetchParams = {
            applicationSuiteId: suiteId ?? '',
            tenantId: tenantId ?? '',
            licenseId: licenseId ?? ''
        };
        dispatch(fetchLicenseDetails(fetchParams))
            .unwrap()
            .then((result) => {
                dispatch(setPageInfo(result));
            })
            .catch((err) => {
                setError(err.message);
            })
            .finally(() => {
                setProcessing(false);
            });
    }, []);

    const getTabLink = (key: string) => (
        `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Licenses}/${licenseId}/${key}`
    );

    const handleChangeTab = (_event: SyntheticEvent, newValue: Routes) => {
        dispatch(setPaginationGridData({}));
        setCurrentTab(newValue);
    };

    useEffect(() => {
        if (!processing && licenseDetails?.id) {
            const licenseDetailsItemsConfig = _createLicenseDetailsHeaderRowItemsConfig(licenseDetails);
            const licenseDetailsCustomLabelItems = licenseDetailsItemsConfig
                .map((configItem) => (
                    <LicenseDetailsHeaderColumn key={configItem.map(item => item.resourceName).join('-')} rowItems={configItem}/>
                ));
            setLicenseDetailsCustomLabelItemsToRender(licenseDetailsCustomLabelItems);
        }
    }, [processing, licenseDetails?.id]);

    if (is404Error(error)) {
        return <NotFound useSpacing/>;
    }

    if (!processing && (licenseDetails?.id && !licenseDetails?.tenantRequiresLicense)) {
        return <NotFound useSpacing/>;
    }

    if (isSomethingWentWrongError(error)) {
        return <p>Something went wrong</p>;
    }

    if(processing) {
        return <CircularProgress color='primary'/>;
    }

    return (
        <Page
            title={`${licenseDetails?.billingRecord}`}
            header={(
                <Typography variant="h5" gutterBottom component="div">
                    {licenseDetails?.id &&
                        <StyledBreadcrumbs
                            guidNameMappings={[{
                                resourceId: suiteId ?? '',
                                resourceName: licenseDetails?.applicationSuiteName || ''
                            }, {
                                resourceId: tenantId ?? '',
                                resourceName: licenseDetails?.tenantName || ''
                            }, {
                                resourceId: licenseId ?? '',
                                resourceName: licenseDetails?.billingRecord ?? ''
                            }]}
                        /> }

                    {licenseDetails?.id && licenseDetails.billingRecord}

                    { licenseDetails?.id &&
                    <Stack direction="row" spacing={4} style={{ marginTop: '10px' }}>
                        {licenseDetailsCustomLabelItemsToRender}
                    </Stack>
                    }
                </Typography>
            )}>
            <Tabs
                sx={{ mb: '-60px', zIndex:1200, width: '40rem' }}
                onChange={handleChangeTab}
                value={currentTab}
            >
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.Users)}
                    key={Routes.Users}
                    link={getTabLink(Routes.Users)}
                    value={Routes.Users}
                />
                <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.UserGroups)}
                    key={Routes.UserGroups}
                    link={getTabLink(Routes.UserGroups)}
                    value={Routes.UserGroups}
                />
                {displayAclTab && <StyledTab
                    disabled={false}
                    label={RoutesNameMap.get(Routes.AclEntries)}
                    key={Routes.AclEntries}
                    link={getTabLink(Routes.AclEntries)}
                    value={Routes.AclEntries}
                />}
            </Tabs>
            <Outlet />
        </Page>
    );
}
